import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, FileText, User, CreditCard, Settings, Bot, Mail, AlertTriangle, HelpCircle } from 'lucide-react';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';

type Tutorial = {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  content: string;
};

const tutorials: Tutorial[] = [
  {
    id: 'getting-started',
    title: '🚀 Premiers pas avec Invoice AI',
    description: 'Découvrez comment configurer votre compte et commencer à utiliser Invoice AI.',
    category: 'Démarrage',
    icon: <Bot className="h-6 w-6 text-primary-500" />,
    content: `# Bienvenue sur Invoice AI !

Pour bien démarrer avec notre plateforme, suivez ces étapes simples :

## 1. Configuration de votre compte

1. **Informations de l'entreprise**
   - Accédez aux paramètres du compte
   - Remplissez les informations de votre entreprise (nom, adresse, SIRET, etc.)
   - Ajoutez votre logo pour personnaliser vos documents

2. **Paramètres de facturation**
   - Configurez votre numérotation de factures et devis
   - Définissez votre devise par défaut
   - Paramétrez votre taux de TVA

## 2. Familiarisez-vous avec le tableau de bord

Le tableau de bord vous donne un aperçu de :
- Vos revenus totaux
- Vos derniers documents créés
- Les paiements en attente
- Les statistiques d'utilisation

## 3. Prochaines étapes recommandées

1. Ajoutez vos premiers clients
2. Créez votre première facture
3. Explorez les fonctionnalités d'IA
4. Personnalisez vos modèles de documents

## Besoin d'aide ?

N'hésitez pas à consulter nos autres guides ou à contacter notre support à contact@invoice-ai.fr`
  },
  {
    id: 'create-invoice',
    title: '📄 Créer votre première facture',
    description: 'Apprenez à générer une facture professionnelle en quelques clics.',
    category: 'Documents',
    icon: <FileText className="h-6 w-6 text-primary-500" />,
    content: `# Créer une facture avec Invoice AI

Suivez ce guide pour créer votre première facture en quelques étapes simples.

## 1. Démarrer une nouvelle facture

1. Cliquez sur "Nouveau Document" dans le menu
2. Sélectionnez "Facture" comme type de document
3. Choisissez le client (ou créez-en un nouveau)

## 2. Utiliser l'IA pour générer le contenu

1. Décrivez votre prestation dans le champ dédié
   Exemple : "Développement d'un site web e-commerce avec intégration de paiement"

2. L'IA va automatiquement :
   - Générer une description détaillée
   - Proposer une structure de lignes
   - Suggérer des prix basés sur le marché

## 3. Personnaliser votre facture

- Ajustez les montants si nécessaire
- Modifiez les descriptions générées
- Ajoutez ou supprimez des lignes
- Définissez la date d'échéance

## 4. Finaliser et envoyer

1. Prévisualisez votre facture
2. Choisissez d'appliquer ou non la TVA
3. Envoyez directement par email ou téléchargez en PDF

## Astuces

- Utilisez des mots-clés précis pour de meilleurs résultats IA
- Sauvegardez des modèles pour vos factures récurrentes
- Activez les rappels automatiques pour les factures impayées

## Fonctionnalités avancées

- Numérotation automatique
- Calcul automatique de la TVA
- Conversion automatique des devises
- Suivi des paiements`
  },
  {
    id: 'manage-clients',
    title: '👥 Gérer vos clients',
    description: 'Comment ajouter et gérer efficacement votre base clients.',
    category: 'Clients',
    icon: <User className="h-6 w-6 text-primary-500" />,
    content: `# Gestion des clients

Apprenez à gérer efficacement votre base clients avec Invoice AI.

## 1. Ajouter un nouveau client

### Informations requises :
- Nom du contact
- Email
- Adresse
- Numéro de téléphone

### Informations optionnelles :
- Nom de l'entreprise
- SIRET
- Numéro de TVA
- Notes personnalisées

## 2. Organiser vos clients

- Recherchez rapidement par nom ou entreprise
- Filtrez par statut (actif/inactif)
- Accédez à l'historique des documents
- Consultez les statistiques par client

## 3. Gérer les informations

- Mettez à jour les coordonnées
- Suivez l'historique des paiements
- Consultez les factures en attente
- Gérez les devis en cours

## 4. Bonnes pratiques

1. Maintenez les informations à jour
2. Vérifiez régulièrement les coordonnées
3. Utilisez des notes pour les informations importantes
4. Suivez les préférences de facturation

## 5. Fonctionnalités avancées

- Export des données clients
- Import en masse
- Segmentation de la clientèle
- Rapports personnalisés`
  },
  {
    id: 'subscription',
    title: '💳 Gérer votre abonnement',
    description: 'Tout savoir sur la gestion de votre abonnement et la facturation.',
    category: 'Compte',
    icon: <CreditCard className="h-6 w-6 text-primary-500" />,
    content: `# Gestion de votre abonnement

Découvrez comment gérer votre abonnement Invoice AI efficacement.

## Plans disponibles

### Plan Essentiel
- 40 documents par mois
- Fonctionnalités de base
- Support par email
- 31€/mois (annuel) ou 37€/mois

### Plan Premium
- Documents illimités
- Toutes les fonctionnalités
- Support prioritaire
- 39€/mois (annuel) ou 47€/mois

## Gérer votre abonnement

1. **Accéder aux paramètres**
   - Cliquez sur votre profil
   - Sélectionnez "Paramètres"
   - Onglet "Abonnement"

2. **Modifier votre plan**
   - Passez à Premium
   - Revenez à Essentiel
   - Changez la périodicité

3. **Moyens de paiement**
   - Ajoutez une carte
   - Modifiez le moyen de paiement
   - Consultez l'historique

## Facturation

- Factures automatiques
- Historique des paiements
- Reçus téléchargeables
- Changement d'adresse de facturation

## Questions fréquentes

1. **Comment annuler ?**
   - Possible à tout moment
   - Effet fin de période
   - Données conservées 30 jours

2. **Changement de plan**
   - Mise à niveau immédiate
   - Prorata temporis
   - Conservation des données`
  },
  {
    id: 'customize-documents',
    title: '✨ Personnaliser vos documents',
    description: 'Apprenez à personnaliser l\'apparence de vos factures et devis.',
    category: 'Documents',
    icon: <Settings className="h-6 w-6 text-primary-500" />,
    content: `# Personnalisation des documents

Découvrez comment personnaliser vos factures et devis pour refléter votre image professionnelle.

## 1. Personnalisation de base

### Logo et identité visuelle
- Ajoutez votre logo
- Choisissez son emplacement
- Définissez sa taille

### En-tête et pied de page
- Informations de l'entreprise
- Mentions légales
- Coordonnées bancaires

## 2. Mise en page

### Structure du document
- Organisation des sections
- Espacement
- Marges

### Éléments visuels
- Couleurs de l'entreprise
- Police de caractères
- Taille des textes

## 3. Contenu personnalisé

### Messages personnalisés
- Notes de bas de page
- Conditions de paiement
- Messages de remerciement

### Champs personnalisés
- Références internes
- Codes projets
- Informations spécifiques

## 4. Modèles

### Création de modèles
- Sauvegarde des préférences
- Modèles par type de service
- Modèles par client

### Gestion des modèles
- Modification
- Duplication
- Archivage

## 5. Conseils pratiques

1. Gardez une mise en page claire
2. Respectez votre charte graphique
3. Testez avant d'envoyer
4. Sauvegardez vos modèles préférés`
  },
  {
    id: 'ai-features',
    title: '🤖 Fonctionnalités IA',
    description: 'Découvrez comment l\'IA peut vous aider à créer des documents plus rapidement.',
    category: 'Démarrage',
    icon: <Bot className="h-6 w-6 text-primary-500" />,
    content: `# Fonctionnalités d'Intelligence Artificielle

Découvrez comment l'IA d'Invoice AI révolutionne la création de vos documents.

## 1. Génération de contenu

### Description automatique
- Transforme vos notes en descriptions professionnelles
- Adapte le langage au secteur d'activité
- Suggère des formulations appropriées

### Structuration intelligente
- Organise les informations logiquement
- Crée des lignes détaillées
- Propose des catégories pertinentes

## 2. Suggestions de prix

### Analyse de marché
- Suggestions basées sur votre secteur
- Adaptation aux standards du marché
- Prise en compte de la complexité

### Optimisation des tarifs
- Recommandations personnalisées
- Cohérence des prix
- Marges optimisées

## 3. Automatisation intelligente

### Reconnaissance de données
- Extraction d'informations
- Remplissage automatique
- Vérification de cohérence

### Apprentissage continu
- S'adapte à vos habitudes
- Améliore les suggestions
- Personnalise les résultats

## 4. Meilleures pratiques

1. **Descriptions précises**
   - Utilisez des mots-clés pertinents
   - Soyez spécifique
   - Incluez les détails importants

2. **Optimisation des résultats**
   - Affinez les suggestions
   - Validez les contenus
   - Personnalisez selon vos besoins`
  },
  {
    id: 'send-documents',
    title: '📧 Envoyer des documents',
    description: 'Comment envoyer vos documents directement depuis la plateforme.',
    category: 'Documents',
    icon: <Mail className="h-6 w-6 text-primary-500" />,
    content: `# Envoi de documents

Apprenez à envoyer vos documents professionnellement depuis Invoice AI.

## 1. Méthodes d'envoi

### Email direct
- Envoi depuis la plateforme
- Personnalisation du message
- Suivi de lecture

### Export PDF
- Téléchargement haute qualité
- Format optimisé
- Compression intelligente

## 2. Personnalisation de l'envoi

### Message d'accompagnement
- Modèles personnalisables
- Variables automatiques
- Signature professionnelle

### Options d'envoi
- Copie à d'autres destinataires
- Programmation d'envoi
- Rappels automatiques

## 3. Suivi des envois

### Tableau de bord
- Statut des envois
- Historique des communications
- Notifications de lecture

### Rappels et relances
- Configuration des délais
- Messages automatiques
- Suivi des réponses

## 4. Bonnes pratiques

1. **Avant l'envoi**
   - Vérifiez le contenu
   - Validez les montants
   - Contrôlez les coordonnées

2. **Message professionnel**
   - Ton approprié
   - Informations essentielles
   - Coordonnées de contact

3. **Suivi**
   - Confirmez la réception
   - Planifiez les relances
   - Documentez les échanges`
  },
  {
    id: 'troubleshooting',
    title: '🔧 Résolution des problèmes',
    description: 'Solutions aux problèmes les plus courants.',
    category: 'Support',
    icon: <AlertTriangle className="h-6 w-6 text-primary-500" />,
    content: `# Guide de dépannage

Solutions aux problèmes fréquemment rencontrés sur Invoice AI.

## 1. Problèmes de connexion

### Impossible de se connecter
1. Vérifiez votre email et mot de passe
2. Effacez le cache du navigateur
3. Utilisez le lien "Mot de passe oublié"

### Compte bloqué
1. Attendez 15 minutes
2. Contactez le support
3. Vérifiez vos emails

## 2. Problèmes de génération

### L'IA ne répond pas
1. Rafraîchissez la page
2. Vérifiez votre connexion
3. Réessayez dans quelques minutes

### Résultats incorrects
1. Précisez votre demande
2. Utilisez des mots-clés spécifiques
3. Ajustez manuellement

## 3. Problèmes d'affichage

### PDF ne s'affiche pas
1. Mettez à jour votre navigateur
2. Essayez un autre navigateur
3. Téléchargez directement

### Mise en page incorrecte
1. Vérifiez les paramètres
2. Réinitialisez le modèle
3. Contactez le support

## 4. Problèmes de paiement

### Carte refusée
1. Vérifiez les informations
2. Contactez votre banque
3. Essayez une autre carte

### Facture manquante
1. Vérifiez vos emails
2. Consultez l'historique
3. Contactez la comptabilité

## Contact support

Si le problème persiste :
- Email : support@invoice-ai.fr
- Délai de réponse : 24h
- Support prioritaire pour Premium`
  }
];

const categories = [
  { id: 'all', name: 'Tout' },
  { id: 'Démarrage', name: '🚀 Démarrage' },
  { id: 'Documents', name: '📄 Documents' },
  { id: 'Clients', name: '👥 Clients' },
  { id: 'Compte', name: '⚙️ Compte' },
  { id: 'Support', name: '🔧 Support' }
];

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial | null>(null);

  const filteredTutorials = tutorials.filter(tutorial => {
    const matchesSearch = tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tutorial.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tutorial.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (selectedTutorial) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 flex items-center">
            <Button 
              variant="ghost" 
              className="mr-4"
              onClick={() => setSelectedTutorial(null)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">{selectedTutorial.title}</h1>
          </div>

          <Card>
            <Card.Content>
              <div className="prose dark:prose-invert max-w-none">
                <div 
                  className="markdown-content"
                  dangerouslySetInnerHTML={{ 
                    __html: selectedTutorial.content
                      .split('\n')
                      .map(line => {
                        if (line.startsWith('# ')) {
                          return `<h1 class="text-3xl font-bold mb-6">${line.substring(2)}</h1>`;
                        }
                        if (line.startsWith('## ')) {
                          return `<h2 class="text-2xl font-semibold mt-8 mb-4">${line.substring(3)}</h2>`;
                        }
                        if (line.startsWith('### ')) {
                          return `<h3 class="text-xl font-semibold mt-6 mb-3">${line.substring(4)}</h3>`;
                        }
                        if (line.startsWith('- ')) {
                          return `<li class="ml-4 text-gray-600 dark:text-gray-400">${line.substring(2)}</li>`;
                        }
                        if (line.startsWith('1. ')) {
                          return `<li class="ml-4 text-gray-600 dark:text-gray-400">${line.substring(3)}</li>`;
                        }
                        if (line.trim() === '') {
                          return '<br>';
                        }
                        return `<p class="text-gray-600 dark:text-gray-400 mb-4">${line}</p>`;
                      })
                      .join('\n')
                  }}
                />
              </div>
            </Card.Content>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-center">
          <Button 
            variant="ghost" 
            className="mr-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Centre d'aide</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Trouvez toutes les réponses à vos questions sur Invoice AI
            </p>
          </div>
        </div>

        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Rechercher dans l'aide..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map(category => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'primary' : 'outline'}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredTutorials.map(tutorial => (
            <Card 
              key={tutorial.id}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setSelectedTutorial(tutorial)}
            >
              <Card.Content>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                      {tutorial.icon}
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold mb-1">{tutorial.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      {tutorial.description}
                    </p>
                  </div>
                </div>
              </Card.Content>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Card>
            <Card.Content>
              <div className="flex flex-col items-center">
                <div className="h-12 w-12 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-4">
                  <HelpCircle className="h-6 w-6 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Besoin d'aide supplémentaire ?</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Notre équipe est là pour vous aider
                </p>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = 'mailto:contact@invoice-ai.fr'}
                >
                  Contactez-nous
                </Button>
              </div>
            </Card.Content>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;